import {
  CompanyType,
  SessionDimensions,
  SessionMetrics,
  SessionMetricsResponse,
  useSessionMetrics,
} from '@va/dashboard/api-client/sessions.api';
import { getInstanceId } from '@va/dashboard/selectors/app';
import { getGlobalFilter } from '@va/dashboard/selectors/ui';
import { Where } from '@va/types/filters';
import { Sorting } from '@va/types/requests';
import { toQueryString } from '@va/util/helpers';
import { useLazyDataFetch } from '@va/util/hooks';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

export type CompanySessionsItem = SessionMetricsResponse<(typeof METRICS)[number], Dimensions>;

type Query<T extends string = string> = {
  fetchCompanyName?: boolean;
  shouldFetch?: boolean;
  pagination: { page: number; pageSize: number };
  order?: Sorting<T>[];
  where?: Where;
};

const COMPANY_NAME_DIMENSION: SessionDimensions = 'companyOrgName' as const;
const COMPANY_TYPE_DIMENSION: SessionDimensions = 'companyOrgType' as const;
type Dimensions = typeof COMPANY_NAME_DIMENSION | typeof COMPANY_TYPE_DIMENSION;
const METRICS = ['visitCount', 'sessionCount', 'sessionPercentOfTotal'] satisfies SessionMetrics[];

export const useVisitsByCompany = ({ fetchCompanyName, shouldFetch, ...query }: Query<(typeof METRICS)[number]>) => {
  return useSessionMetrics(
    {
      trace: 'companies',
      dimensions: [COMPANY_TYPE_DIMENSION, ...(fetchCompanyName ? [COMPANY_NAME_DIMENSION] : [])],
      metrics: METRICS,
      ...query,
    },
    shouldFetch,
  );
};

export type CompanyVisitItem = {
  visits: number;
  companyOrgName: string;
  companyType: CompanyType;
  visitsPercentage: number;
};

type VisitsByCompanyResponse = {
  payload: CompanyVisitItem[];
  meta: {
    total: number;
    page: number;
    pageTotal: number;
    pageSize: number;
  };
};

type VisitsByCompanyQuery = {
  companyOrgName?: string;
  companyCategory?: 'from' | 'via';
  page: number;
  hideCompanyOrgName: boolean;
  pageSize: number;
};

export const useVisitsByCompanyOld = (queryFilter: VisitsByCompanyQuery, shouldFetchData = true) => {
  const { from, until } = useSelector(getGlobalFilter);
  const { companyOrgName, companyCategory, page, pageSize, hideCompanyOrgName } = queryFilter;
  const instanceId = useSelector(getInstanceId);

  const query = useMemo(
    () => ({
      from: from.toISOString(),
      until: until.toISOString(),
      companyOrgName,
      companyCategory,
      page,
      hideCompanyOrgName,
      pageSize,
    }),
    [from, until, companyOrgName, companyCategory, page, hideCompanyOrgName, pageSize],
  );

  const mapper = useCallback((response: VisitsByCompanyResponse) => {
    const { payload, meta } = response;
    return { items: payload ?? [], meta };
  }, []);

  return useLazyDataFetch<{
    items: Array<CompanyVisitItem>;
    meta: {
      total: number;
      page: number;
      pageTotal: number;
      pageSize: number;
    };
  }>(
    shouldFetchData ? `/v2/websites/${instanceId}/visits-by-company?${toQueryString(query)}` : null,
    { revalidateOnFocus: false },
    mapper,
  );
};
